import React, { createContext, useMemo, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../authentication';
import { useGetTermTranslationsQuery } from '../hooks';
import { useOrganizations } from '../../organization/providers';
import {
  useCreateTermTranslationMutation,
  useCreateTranslationMutation
} from '../../organization/hooks';
import { useNotifications } from '../../../providers/NotificationsProvider';

const TermTranslationsContext = createContext();

const TermTranslationsProvider = ({ children = null }) => {
  const { accessToken } = useAuth();
  const { currentOrganization } = useOrganizations();
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();
  const [translatedTerm, setTranslatedTerm] = useState(null);

  const activeTermTranslationsQuery = useGetTermTranslationsQuery(
    accessToken,
    currentOrganization?.id,
    'active'
  );
  const pendingTermTranslationsQuery = useGetTermTranslationsQuery(
    accessToken,
    currentOrganization?.id,
    'is_reviewing'
  );
  const createTermTranslationMutation = useCreateTermTranslationMutation(accessToken);
  const createTranslationMutation = useCreateTranslationMutation(accessToken);

  const filteredActiveTermTranslations = useMemo(
    () => activeTermTranslationsQuery.data?.filter((term) => term?.termList[0] !== '*'),
    [activeTermTranslationsQuery.data]
  );

  const filteredPendingTermTranslations = useMemo(
    () => pendingTermTranslationsQuery.data?.filter((term) => term?.termList[0] !== '*'),
    [pendingTermTranslationsQuery.data]
  );

  const metrics = filteredActiveTermTranslations
    ?.filter((term) => term?.type === 'metric')
    .map((item) => ({ value: item.termList[0], definition: item.displayedDescription }));

  const createTermTranslation = async (definition) => {
    if (!definition.trim()) {
      return;
    }

    try {
      const newTerm = await createTermTranslationMutation.mutateAsync({
        organizationId: currentOrganization?.id,
        term: '()',
        translation: definition.trim()
      });

      // Update the pending query cache with the new term
      const queryKey = ['chat', 'termTranslations', 'is_reviewing'];
      queryClient.setQueryData(queryKey, (old) => {
        if (!old) return [newTerm];
        return [...old, newTerm];
      });

      addNotification({
        type: 'success',
        message: `${newTerm.translation} was created`,
        highlight: newTerm.translation
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to add business logic. Please try again.'
      });
      throw error;
    }
  };

  const createTranslation = async (definition) => {
    if (!definition.trim()) {
      return;
    }

    try {
      const result = await createTranslationMutation.mutateAsync({
        organizationId: currentOrganization?.id,
        translation: definition.trim()
      });

      // Set the translated term to keep the loader visible with success state
      setTranslatedTerm(result);
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to create translation. Please try again.'
      });
    }
  };

  const clearTranslatedTerm = () => {
    setTranslatedTerm(null);
  };

  const value = useMemo(
    () => ({
      activeTermTranslations: filteredActiveTermTranslations,
      pendingTermTranslations: filteredPendingTermTranslations,
      termTranslations: filteredActiveTermTranslations,
      metrics,
      createTermTranslation,
      createTranslation,
      isCreating: createTermTranslationMutation.isLoading,
      isTranslating: createTranslationMutation.isLoading,
      translatedTerm,
      clearTranslatedTerm
    }),
    [
      filteredActiveTermTranslations,
      filteredPendingTermTranslations,
      metrics,
      createTermTranslation,
      createTranslation,
      createTermTranslationMutation.isLoading,
      createTranslationMutation.isLoading,
      translatedTerm,
      clearTranslatedTerm
    ]
  );

  if (activeTermTranslationsQuery.isInitialLoading || pendingTermTranslationsQuery.isInitialLoading)
    return null;

  return (
    <TermTranslationsContext.Provider value={value}>{children}</TermTranslationsContext.Provider>
  );
};

TermTranslationsProvider.propTypes = {
  children: PropTypes.node
};

const useTermTranslations = () => useContext(TermTranslationsContext);

export { TermTranslationsProvider, TermTranslationsContext, useTermTranslations };
